// @ts-check
// Configurações > Funil e etapas > Campos obrigatórios para avançar (contrato 08 do modelo de dados, seção 6): cada campo
// que os relatórios precisam, a etapa a partir da qual vale e o liga/desliga. Só quem tem a flag DATA_MODEL (o
// administrador, nos papéis padrão) altera; os demais veem só leitura. O motor confere de novo.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { fetchStageRequirements, updateStageRequirement } from '../../../data/stage-requirements.js';
import { getSortedOptions } from '../../../lib/field-metadata.js';
import { Skeleton, Toggle } from '../../../ui/feedback.js';
import { Icon } from '../../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../../i18n/index.js';
import { SettingsSection } from '../settings-pages.js';

const EXCLUDED_FROM_STAGES = new Set(['LEAD', 'LOST']);

/**
 * @typedef {import('../../../data/stage-requirements.js').StageRequirement} StageRequirement
 * @param {{ stageField: import('../../../lib/field-metadata.js').FieldMetadata }} props
 */
export function StageRequirementsSettings({ stageField }) {
  const [state, setState] = useState(/** @type {{ stageRequirements: StageRequirement[], canEdit: boolean } | null} */ (null));
  const [hasError, setHasError] = useState(false);
  const [savingKey, setSavingKey] = useState(/** @type {string | null} */ (null));

  useEffect(() => {
    fetchStageRequirements().then(setState).catch(() => setHasError(true));
  }, [stageField.id]);

  const stageOptions = getSortedOptions(stageField).filter((option) => !EXCLUDED_FROM_STAGES.has(option.value));
  const stageLabel = (/** @type {string | null} */ value) =>
    translateMetadataLabel(stageField.options?.find((option) => option.value === value)?.label ?? value ?? '');

  /** @param {StageRequirement} requirement @param {{ isEnabled?: boolean, fromStage?: string | null }} patch */
  const save = async (requirement, patch) => {
    setSavingKey(requirement.key);
    try {
      const updated = await updateStageRequirement(requirement.key, patch);
      setState((current) => current && {
        ...current,
        stageRequirements: current.stageRequirements.map((item) => (item.key === updated.key ? updated : item)),
      });
      showSuccessSnackbar(t('stageRequirements.saved'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setSavingKey(null);
    }
  };

  let content;
  if (hasError) content = html`<p class="data-model-empty-hint">${t('stageRequirements.loadError')}</p>`;
  else if (!state) content = html`<${Skeleton} height=${240} radius=${8} />`;
  else {
    const isEditable = state.canEdit;
    content = html`<div class="stage-requirements-table" role="table" aria-label=${t('stageRequirements.settingsTitle')}>
      <div class="stage-requirements-row stage-requirements-head" role="row">
        <span role="columnheader">${t('stageRequirements.columnField')}</span>
        <span role="columnheader">${t('stageRequirements.columnFromStage')}</span>
        <span role="columnheader">${t('stageRequirements.columnEnabled')}</span>
      </div>
      ${state.stageRequirements.map((requirement) => html`<div key=${requirement.key} class="stage-requirements-row" role="row"
          data-enabled=${requirement.isEnabled ? 'true' : 'false'}>
        <span class="stage-requirements-name" role="cell">
          <strong>${t(`stageRequirements.rules.${requirement.key}.label`)}</strong>
          <small>${t(`stageRequirements.scope.${requirement.scope}`)} · ${t(`stageRequirements.rules.${requirement.key}.reason`)}</small>
        </span>
        <span role="cell">
          <select class="text-input stage-requirements-select" disabled=${!isEditable || savingKey === requirement.key}
            aria-label=${`${t('stageRequirements.columnFromStage')}: ${t(`stageRequirements.rules.${requirement.key}.label`)}`}
            value=${requirement.fromStage ?? ''}
            onChange=${(/** @type {Event} */ event) => {
              const value = /** @type {HTMLSelectElement} */ (event.currentTarget).value;
              save(requirement, { fromStage: value === '' ? null : value });
            }}>
            <option value="">${t('stageRequirements.defaultStage', { stage: stageLabel(requirement.defaultFromStage) })}</option>
            ${stageOptions.map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
          </select>
        </span>
        <span role="cell">
          <${Toggle} value=${requirement.isEnabled} disabled=${!isEditable || savingKey === requirement.key}
            ariaLabel=${`${t('stageRequirements.columnEnabled')}: ${t(`stageRequirements.rules.${requirement.key}.label`)}`}
            onChange=${isEditable ? (/** @type {boolean} */ isEnabled) => save(requirement, { isEnabled }) : undefined} />
        </span>
      </div>`)}
    </div>
    ${!isEditable ? html`<p class="input-hint"><${Icon} name="IconLock" size=${12} /> ${t('stageRequirements.adminOnly')}</p>` : null}`;
  }

  return html`<${SettingsSection} title=${t('stageRequirements.settingsTitle')} description=${t('stageRequirements.settingsDescription')}>
    ${content}
  <//>`;
}
